import React, { useState } from "react";
import styled from "styled-components";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { FaTimes, FaClipboardCheck, FaExclamationCircle, FaFilePdf } from "react-icons/fa";
import LogoMexicoGente from "../magenes/LogoMexicoGente.png";

const guinda = "#611232";

export default function ConsultarExpediente({ isOpen, onClose }) {
  const [formData, setFormData] = useState({ nombre: "", folio: "", claveSeguridad: "" });
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (!isOpen) {
      setFormData({ nombre: "", folio: "", claveSeguridad: "" });
      setResultado(null);
      setError("");
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const consultar = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.folio || !formData.claveSeguridad) return;

    setLoading(true);
    setError("");
    setResultado(null);

    try {
      const q = query(
        collection(db, "expedientes"),
        where("folio", "==", formData.folio.trim())
      );
      const snap = await getDocs(q);

      if (!snap.empty) {
        // Verificar nombre y clave
        const docData = snap.docs[0].data();
        if (
          docData.nombre.toLowerCase().trim() === formData.nombre.toLowerCase().trim() &&
          docData.claveSeguridad.trim() === formData.claveSeguridad.trim()
        ) {
          setResultado(docData);
        } else {
          setError("Los datos ingresados no coinciden con nuestros registros.");
        }
      } else {
        setError("No se encontró ningún expediente con ese número de folio.");
      }
    } catch (err) {
      setError("Error al conectar con el servidor. Intente más tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPdf = () => {
    if (resultado && resultado.pdfUrl) {
      window.open(resultado.pdfUrl, "_blank");
    } else {
      setError("Este expediente aún no tiene un archivo PDF adjunto.");
    }
  };

  return (
    <Overlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>

        <IconContainer>
          <IconWrapper>
            <FaClipboardCheck />
          </IconWrapper>
        </IconContainer>

        {!resultado ? (
          <>
            <Titulo>Consultar expediente</Titulo>
            <Form onSubmit={consultar}>
              <FormGroup>
                <Label>Ingresa tu nombre completo</Label>
                <Input
                  placeholder="Ej: Ernesto Juarez Gomez"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Ingresa tu folio</Label>
                <Input
                  placeholder="Ej: NUC0000000AP"
                  value={formData.folio}
                  onChange={(e) => setFormData({ ...formData, folio: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Ingresa tu clave de seguridad</Label>
                <Input
                  placeholder="Ej: RM0000"
                  value={formData.claveSeguridad}
                  onChange={(e) => setFormData({ ...formData, claveSeguridad: e.target.value })}
                  required
                />
              </FormGroup>

              {error && (
                <ErrorMessage>
                  <FaExclamationCircle /> {error}
                </ErrorMessage>
              )}

              <SubmitButton type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
                {loading ? "Consultando..." : "Ingresar al expediente"}
              </SubmitButton>
            </Form>
          </>
        ) : (
          <ResultViewContainer>
            <HolaText>Hola, {resultado.nombre}</HolaText>
            
            {error && (
                <ErrorMessage style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                  <FaExclamationCircle /> {error}
                </ErrorMessage>
            )}

            <AbrirButton onClick={handleOpenPdf}>
              <FaFilePdf size={20} />
              Abrir
            </AbrirButton>

            <LogoContainer>
              <img src={LogoMexicoGente} alt="Logo" style={{ width: '150px' }} />
            </LogoContainer>

          </ResultViewContainer>
        )}
      </ModalContent>
    </Overlay>
  );
}

// Estilos
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 1.5rem;
`;

const ModalContent = styled.div`
  background: white;
  width: 100%;
  max-width: 450px;
  padding: 3rem 2.5rem;
  border-radius: 2rem;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ccc;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: #333; }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

const IconWrapper = styled.div`
  width: 65px;
  height: 65px;
  background: ${guinda};
  color: white;
  border-radius: 20px;
  display: grid;
  place-items: center;
  font-size: 1.8rem;
`;

const Titulo = styled.h2`
  margin: 0 0 2rem;
  font-weight: 900;
  color: #1a1a1a;
  font-size: 1.4rem;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #777;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  border: 1.5px solid #e0e0e0;
  border-radius: 15px;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: ${guinda};
    box-shadow: 0 0 0 4px rgba(97, 18, 50, 0.1);
  }
  &::placeholder {
    color: #b3b3b3;
    font-weight: 400;
  }
`;

const SubmitButton = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  border-radius: 15px;
  padding: 1.2rem;
  font-size: 1.1rem;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  box-shadow: 0 10px 20px rgba(97, 18, 50, 0.15);

  &:hover {
    background: #4a0d26;
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(97, 18, 50, 0.25);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d93025;
  background: #fdf2f2;
  padding: 0.8rem;
  border-radius: 10px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  line-height: 1.4;
`;

const ResultViewContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const HolaText = styled.h3`
  color: #000;
  font-weight: 900;
  font-size: 1.3rem;
  margin-bottom: 2rem;
`;

const AbrirButton = styled.button`
  background: ${guinda};
  color: white;
  border: none;
  border-radius: 100px;
  padding: 1rem 3rem;
  font-size: 1.2rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  transition: all 0.3s;
  box-shadow: 0 10px 20px rgba(97, 18, 50, 0.15);

  &:hover {
    background: #4a0d26;
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(97, 18, 50, 0.25);
  }
`;

const LogoContainer = styled.div`
  margin-top: 3rem;
  display: flex;
  justify-content: center;
`;
